import React, { useState, useRef } from 'react';
import {
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  CssBaseline,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  Chip,
  Alert,
  Stack,
} from "@mui/material";
import {
  Send as SendIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  AttachFile as AttachFileIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import Attachment from './Attachment';
import axios from 'axios';
import './custom.css';

const subjectType = [
  { value: 'Claims', label: 'Claims' },
  { value: 'Eligbility', label: 'Eligbility' },
  { value: 'Benefits', label: 'Benefits' },
  { value: 'Providers', label: 'Providers' },
  { value: 'Other', label: 'Other' }
];

const coverageType = [
  { value: 'Vision', label: 'Vision' }
];

const Compose = ({ mid, name, email, hideCompose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const attachmentRef = useRef();
  
  const [memberId, setMemberId] = useState("");
  const [telNum, setTelNum] = useState("+1");
  const [invalidMobile, setInvalidMobile] = useState(false);
  const [bestTime, setBestTime] = useState("");
  const [coverage, setCoverage] = useState("Vision");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState([]);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  const MAX_MESSAGE_LENGTH = 2000;
  const isFormValid = memberId && telNum && !invalidMobile && subject && message && !submitButtonDisabled && message.length <= MAX_MESSAGE_LENGTH;

  const getAttachmentId = (id) => {
    const att = id.map((value) => ({ "attachment_id": value }));
    setAttachment(att);
  };

//   const handleTelNum = (value) => {
//     if (value.length === 17) {
//       setInvalidMobile(false);
//       setSubmitButtonDisabled(false);
//     } else {
//       setInvalidMobile(true);
//       setSubmitButtonDisabled(true);
//     }
//     setTelNum(value);
//   };

const handleTelNum = (value, info) => {
    setTelNum(value);
    const isValid =
      matchIsValidTel(value, { onlyCountries: ["US"] }) &&
      (info?.nationalNumber?.length ?? 0) === 10;
    setInvalidMobile(!isValid);
  };

  const handleCancel = () => {
    setMemberId("");
    setTelNum("+1");
    setBestTime("");
    setSubject("");
    setAttachment([]);
    setMessage("");
    if (attachmentRef.current) {
      attachmentRef.current.handleUploadComplete(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const property = {
      "memberId": memberId,
      "telNum": telNum,
      "bestTime": bestTime,
      "coverage": coverage,
    };

    const messagebody = {
      "sender": { "mid": mid },
      "subject": subject,
      "body": message,
      "recipients": {
        "recipient": {
          "mid": process.env.REACT_APP_RECIEVER_MID
        }
      },
      "attachments": {
        "attachment": attachment
      },
      "attributes": JSON.stringify({
        "agent_display": {
          "member_id": memberId,
          "member_name": name,
          "telephone_number": telNum,
          "best_time": bestTime
        }
      }),
      "properties": JSON.stringify(property)
    };

    const formData = new FormData();
    formData.append("message", JSON.stringify(messagebody));

    axios.post(process.env.REACT_APP_OMS_MESSAGE_URL, formData, {
      headers: {
        'Authorization': "Bearer " + sessionStorage.getItem('token'),
        'Content-Type': 'multipart/form-data',
        'Cache-Control': 'No-Store'
      }
    })
      .then(() => {
        alert("Message Sent Successfully");
        setMemberId("");
        setTelNum("");
        setBestTime("");
        setSubject("");
        setAttachment([]);
        setMessage("");
        if (attachmentRef.current) {
          attachmentRef.current.handleUploadComplete(false);
        }
      })
      .catch(() => {
        alert("There was an error while sending message");
      });
  };

  return (
    <Box>
      <CssBaseline />
      <Box
        sx={{
          marginTop: '60px',
          width: '100%',
          maxWidth: 1200,
          ml: 'auto',
          mr: 'auto',
          px: { xs: 2, sm: 3 },
        }}
      >
        <form id="form" onSubmit={handleSubmit}>
          <Paper
            elevation={3}
            sx={{
              backgroundColor: 'background.paper',
              mt: 2,
              mb: 4,
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
            }}
          >
            {/* Header */}
            <Box sx={{ mb: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
              <Typography 
                component="h1" 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 0.5,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                }}
              >
                Ask a Question
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
              >
                Complete the form below to send a secure message
              </Typography>
            </Box>

            <Divider sx={{ mb: { xs: 2, sm: 2.5 } }} />

            {/* All Fields in One Grid */}
            <Grid container spacing={{ xs: 2, sm: 2.5 }} sx={{
  display: 'flex',
  flexDirection: 'row',
}}>
              
              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  disabled
                  value={name}
                  variant="outlined"
                  id="name"
                  label="Name"
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  disabled
                  value={email}
                  variant="outlined"
                  id="email"
                  label="Email"
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }} >
                <TextField
                  disabled
                  id="coverageType"
                  select
                  fullWidth
                  label="Coverage Type"
                  value={coverage}
                  onChange={(e) => setCoverage(e.target.value)}
                  SelectProps={{ native: true }}
                  variant="outlined"
                  size="small"
                >
                  <option aria-label="None" value="" />
                  {coverageType.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  required
                  id="subject"
                  select
                  fullWidth
                  label="Subject / Topic"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  SelectProps={{ native: true }}
                  variant="outlined"
                  size="small"
                  helperText={subject ? "Topic selected" : "Please select the topic of your inquiry"}
                >
                  <option aria-label="None" value="" />
                  {subjectType.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
              </Grid>

              
              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  required
                  type="number"
                  value={memberId}
                  onChange={(e) => setMemberId(e.target.value)}
                  variant="outlined"
                  id="memberId"
                  name="memberId"
                  label="Member ID"
                  fullWidth
                  size="small"
                  helperText="Enter your member identification number"
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <MuiTelInput
                  required
                  value={telNum}
                  onChange={handleTelNum}
                  defaultCountry="US"
                  forceCallingCode
                  onlyCountries={['US']}
                  id="telNum"
                  label="Telephone Number"
                  fullWidth
                  variant="outlined"
                  size="small"
                  helperText={invalidMobile ? "Please enter a valid 10-digit US phone number" : "We may call you for additional information"}
                  error={invalidMobile}
                  inputProps={{ inputMode: 'tel' }}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <TextField
                  variant="outlined"
                  multiline
                  rows={4}  
                  id="bestTime"
                  name="bestTime"
                  label="Best Time to Reach You"
                  value={bestTime}
                  onChange={(e) => setBestTime(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="e.g., Weekdays 9 AM - 5 PM"
                  helperText="Optional: When are you available?"
                />
              </Grid>
              
              
              <Grid item size={{ xs: 12, md: 8 }}>
                <TextField
                  required
                  variant="outlined"
                  multiline
                  rows={4}
                  fullWidth
                  id="message"
                  name="message"
                  label="How can we help you?"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  slotProps={{
                    maxLength: MAX_MESSAGE_LENGTH 
                  }}
                  
                  helperText={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <span style={{ fontSize: 'inherit' }}>Provide detailed information about your inquiry</span>
                      <Chip 
                        label={`${message.length} / ${MAX_MESSAGE_LENGTH}`} 
                        size="small" 
                        color={message.length > MAX_MESSAGE_LENGTH ? 'error' : 'default'}
                        sx={{ ml: { xs: 0, sm: 1 } }}
                      />
                    </Box>
                  }
                  error={message.length > MAX_MESSAGE_LENGTH}
                  // inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: { xs: 1, sm: 1.5 } }} />
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: { xs: 1, sm: 1.5 }, 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontWeight: 600,
                    fontSize: { xs: '0.813rem', sm: '0.875rem' }
                  }}
                >
                  <AttachFileIcon sx={{ mr: 0.5, fontSize: { xs: 18, sm: 20 }, color: 'primary.main' }} />
                  Attachments
                </Typography>
                <Attachment 
                  ref={attachmentRef} 
                  getAttachmentId={getAttachmentId} 
                  handleSubmitButtonDisabled={setSubmitButtonDisabled} 
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" icon={<InfoIcon />} sx={{ py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: { xs: '0.688rem', sm: '0.75rem' } }}>
                    <strong>File Requirements:</strong> Max 5 MB • Formats: .jpg, .png, .jpeg, .pdf • Recommended resolution: 300 dpi
                  </Typography>
                </Alert>
              </Grid>
            </Grid>

            {/* Important Information */}
            <Box sx={{ mt: { xs: 2, sm: 2.5 }, mb: { xs: 1.5, sm: 2 } }}>
              <Alert severity="warning" icon={<InfoIcon />} sx={{ py: 0.5 }}>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
                  <strong>Response Time:</strong> We will respond within two business days. 
                  Decisions about benefits or appeals will be sent through standard methods.
                </Typography>
              </Alert>
            </Box>

            <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.688rem', sm: '0.75rem' } }}>
                * Required fields &nbsp;&nbsp; ** We may call if more details are needed
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={{ xs: 1.5, sm: 2 }} 
              justifyContent={{ xs: 'center', md: 'flex-end' }}
              sx={{ mt: { xs: 2, sm: 3 } }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                size={isMobile ? "medium" : "large"}
                startIcon={<CancelIcon />}
                sx={{ 
                  textTransform: 'none',
                  minWidth: { xs: '100%', sm: 140 },
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '0.938rem' }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                size={isMobile ? "medium" : "large"}
                endIcon={<SendIcon />}
                disabled={!isFormValid}
                sx={{ 
                  textTransform: 'none',
                  minWidth: { xs: '100%', sm: 140 },
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '0.938rem' },
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 6
                  }
                }}
              >
                Submit
              </Button>
            </Stack>
          </Paper>
        </form>
      </Box>
    </Box>
  );
};

export default Compose;